import React, { useState } from 'react';
import { Search, Plus, Filter } from 'lucide-react';
import { MOCK_PROJECTS } from '../../mock';
import { ProjectStatus } from '../../types';
import Badge from '../../components/common/Badge';

const statusVariant: Record<ProjectStatus, any> = {
  pending: 'warning', scheduled: 'info', in_progress: 'accent',
  completed: 'success', cancelled: 'danger', on_hold: 'warning',
};

const AdminProjects: React.FC = () => {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const filtered = MOCK_PROJECTS.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.customerName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: 'var(--text-primary)', margin: 0 }}>Projects</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input className="input-field" style={{ paddingLeft: '2.25rem', width: '220px' }} placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn-primary" style={{ fontSize: '0.875rem' }}><Plus size={16} /> New Project</button>
        </div>
      </div>

      {view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
          {filtered.map(p => (
            <div key={p.id} className="card card-hover">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', margin: '0 0 0.25rem' }}>{p.title}</h3>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>{p.projectNumber}</p>
                </div>
                <Badge label={p.status.replace('_', ' ')} variant={statusVariant[p.status]} />
              </div>

              {p.progress > 0 && (
                <div style={{ marginBottom: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                    <span>Progress</span><span style={{ color: '#1493e6', fontWeight: 600 }}>{p.progress}%</span>
                  </div>
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: '9999px', height: '6px', overflow: 'hidden' }}>
                    <div style={{ background: p.status === 'completed' ? '#1493e6' : 'linear-gradient(90deg, #1493e6, #1493e6)', height: '100%', width: `${p.progress}%`, borderRadius: '9999px' }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.8rem' }}>
                {[
                  { label: 'Customer', value: p.customerName },
                  { label: 'Contractor', value: p.contractorName || 'Unassigned' },
                  { label: 'Budget', value: `$${p.budget.toLocaleString()}` },
                  { label: 'Start', value: p.startDate },
                ].map(item => (
                  <div key={item.label}>
                    <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button className="btn-ghost" style={{ fontSize: '0.8rem', flex: 1 }}>View Details</button>
                <button className="btn-primary" style={{ fontSize: '0.8rem', flex: 1 }}>Update</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                {['Project', 'Customer', 'Service', 'Budget', 'Progress', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.875rem 1rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.title}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{p.projectNumber}</div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', color: 'var(--text-secondary)' }}>{p.customerName}</td>
                  <td style={{ padding: '0.875rem 1rem', textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{p.service.replace('-', ' ')}</td>
                  <td style={{ padding: '0.875rem 1rem', fontWeight: 700, color: '#1493e6' }}>${p.budget.toLocaleString()}</td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ flex: 1, background: 'var(--bg-secondary)', borderRadius: '9999px', height: '6px', overflow: 'hidden', minWidth: '60px' }}>
                        <div style={{ background: 'linear-gradient(90deg, #1493e6, #1493e6)', height: '100%', width: `${p.progress}%` }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>{p.progress}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <Badge label={p.status.replace('_', ' ')} variant={statusVariant[p.status]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
